import "./CallTable.scss";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import "moment/min/locales";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { CallDetail } from "../../types/call";
import { User } from "../../types/user";
import { CallFilter } from "../../types/filters";
import { Dictionary } from "../../types/Dictionary";
import { ColorCategory } from "../../types/master-data";
import CallListing from "../../components/custom/CallListing";
import CallFilterDrawer from "../../components/CallFilterDrawer";
import LoadingOverlay from "../../components/LoadingOverlay";
import useLocale from "../../hooks/useLocale";
import callsService from "../../services/calls.service";
import usersService from "../../services/users.service";
import { searchCallData } from "../../utils/callDetailUtil";
import { PeriodFilterTypes } from "../../data/PeriodFilterTypes";
import { formatDateTime } from "../../utils/dateTimeUtils";
import { AxiosError } from "axios";
import CustomSearchFieldTextInput from "../../components/custom/form/CustomSearchFieldTextInput";
import useSwal from "../../hooks/useSwal";
import { Page } from "../../types/Page";
import { ApiResponse } from "../../types/ApiResponse";
import { CallTableSortColumnEnum } from "../../enums/CallTableSortColumnEnum";
import { Dates, getDateBasedOnFilterType } from "../../utils/FilterDrawerUtil";
import useTranslation from "../../hooks/useTranslation";
import ExportCallsDialog from "../../components/ExportCallsDialog";
import { AccessPermissionEnum } from "../../enums";
import { SweetAlertOptions } from "sweetalert2";
import recordingsService from "../../services/recordings.service";
import useAuth from "../../hooks/useAuth";
import { InsightCallDetails } from "../../types/insight";
import { orderBy } from "@progress/kendo-data-query";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import BulkDownloadLimitDialog from "../bulk-download/BulkDownloadLimitDialog";

const initialPageDetail: Page = {
  skip: 0,
  take: 10,
  totalRecords: 0,
  sort: {
    sortField: CallTableSortColumnEnum.DateTime,
    sortDirection: "desc",
  },
};

const CallTable: React.FC = () => {
  const trans = useTranslation("calls");
  const localeCtx = useLocale();
  const swal = useSwal();
  const auth = useAuth();
  const [callData, setCallData] = useState<ApiResponse<CallDetail> | undefined>(
    undefined
  );
  const [currentUserData, setCurrentUserData] = useState<User>();
  const [callFilter, setCallFilter] = useState<CallFilter>();
  const [search, setSearch] = useState<string>("");
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  // For checkbox
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
  const [isShowCheckedRows, setIsShowCheckedRows] = useState<boolean>(false);
  const [filteredCalls, setFilteredCalls] = useState<InsightCallDetails | undefined>(undefined);

  const [bulkDownloadSizeLimitMB, setBulkDownloadSizeLimitMB] = useState<number>(2048);
  const [bulkDownloadRowLimit, setBulkDownloadRowLimit] = useState<number>(10);

  useEffect(() => {
    getFilterMasterData();
    const filterValuesCache = sessionStorage.getItem("VoiceAiCallFilter");
    if (filterValuesCache !== null) {
      let filterValues: CallFilter = JSON.parse(filterValuesCache);
      filterValues = { ...filterValues, page: { ...initialPageDetail } };
      setCallFilter(filterValues);
      sendFetchCallsRequest(filterValues);
    } else {
      const startDateTime = new Date();
      const endDateTime = new Date();
      startDateTime?.setHours(0, 0, 0);
      endDateTime?.setHours(23, 59, 59);
      const filterValues: CallFilter = {
        startDate: moment(startDateTime).format("YYYY-MM-DD HH:mm:ss"),
        endDate: moment(endDateTime).format("YYYY-MM-DD HH:mm:ss"),
        periodType: 1,
        filterType: 1,
        page: { ...initialPageDetail },
      };
      setCallFilter(filterValues);
      sendFetchCallsRequest(filterValues);
    }

    let mounted = true;
    const fetchBulkLimits = async () => {
      try {
        const limits = await recordingsService.getBulkDownloadLimits();
        if (mounted && limits) {
          if (typeof limits.bulkDownloadSizeLimitInMbs === "number") {
            setBulkDownloadSizeLimitMB(limits.bulkDownloadSizeLimitInMbs);
          }
          if (typeof limits.bulkDownloadRowCountLimit === "number") {
            setBulkDownloadRowLimit(limits.bulkDownloadRowCountLimit);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch bulk download limits", err);
      }
    };
    fetchBulkLimits();
    return () => {
      mounted = false;
    };

  }, []);

  useEffect(() => {
    if (callFilter) {
      sessionStorage.setItem("VoiceAiCallFilter", JSON.stringify(callFilter));
    }
  }, [callFilter]);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["calls"]) {
      trans.fetchTranslations("calls");
    }
  }, [localeCtx?.selectedLocale]);

  const sendFetchCallsRequest = async (filter: CallFilter) => {
    try {
      setLoading(true);
      setError(false);
      // setCheckedRows([]);
      setCallData({ data: undefined, page: { ...filter.page } });
      if (filter.periodType !== 4) {
        const getFilterBasesDates: Dates = getDateBasedOnFilterType(
          filter.periodType ?? 0,
          filter.filterType ?? 0
        );
        filter = {
          ...filter,
          startDate: moment(getFilterBasesDates.tempStartDate).format(
            "YYYY-MM-DD HH:mm:ss"
          ),
          endDate: moment(getFilterBasesDates.tempEndDate).format(
            "YYYY-MM-DD HH:mm:ss"
          ),
        };
      }
      console.log(filter);
      const fetchedCalls = await callsService.fetchCalls({
        ...filter,
        searchTerm: search,
        periodType: undefined,
      });
      if (fetchedCalls && fetchedCalls.data && fetchedCalls.data.length > 0) {
        setCallData(fetchedCalls);
      } else {
        setCallData({ data: [], page: { ...initialPageDetail } });
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        setError(true);
      }
      setCallData(undefined);
    } finally {
      setLoading(false);
    }
  };

  const getFilterMasterData = async () => {
    try {
      const currentUser = await usersService.getCurrentUser();
      setCurrentUserData(currentUser);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  };

  const handleFilterDialog = () => {
    setFilterVisible(!filterVisible);
  };

  const handleCallDataFilter = (callFilterData: CallFilter) => {
    let _callFilterData: CallFilter = {
      ...callFilterData,
      page: { ...initialPageDetail },
    };
    setCallFilter(_callFilterData);
    setFilterVisible(!filterVisible);
    sendFetchCallsRequest({ ..._callFilterData });
    handleClearAllRows();
  };

  const handleCallFlagUpdate = async (
    selectedFlag: ColorCategory | null,
    callDetailData: CallDetail
  ) => {
    if (selectedFlag === null) {
      selectedFlag = { id: 0, name: "", hexCode: "" };
    }
    if (callData?.data) {
      var tempCalls = callData.data.map((cd) => {
        if (cd.platformCallDetailId === callDetailData.platformCallDetailId) {
          cd.colorCategory = selectedFlag !== null ? selectedFlag : undefined;
        }
        return cd;
      });
      setCallData({ ...callData, data: tempCalls });
    }
  };

  const handlePaginationChange = (pageDetail: Page) => {
    setCallFilter({ ...callFilter, page: pageDetail });
    sendFetchCallsRequest({ ...callFilter, page: pageDetail });
  };

  const handleSortChange = (sortField: string, sortDirection: string) => {
    let _pageFilter: Page = { ...initialPageDetail };
    _pageFilter.sort = {
      sortDirection: sortDirection,
      sortField: sortField,
    };
    setCallFilter({ ...callFilter, page: _pageFilter });
    sendFetchCallsRequest({ ...callFilter, page: _pageFilter });
  };

  const handleSearchTextOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      let _filter: CallFilter = {
        ...callFilter,
        page: { ...initialPageDetail },
      };
      setCallFilter(_filter);
      sendFetchCallsRequest(_filter);
      handleClearAllRows();
    }
  };

  const onExportBtnClick = () => {
    setIsExportDialogOpen(true);
  }

  const onCloseExportDialog = () => {
    setIsExportDialogOpen(false);
  };


  const toggleCheckedRowsView = () => {
    // If currently showing checked rows, just go back to original grid keeping checkedRows intact
    if (isShowCheckedRows) {
      setIsShowCheckedRows(false);
      return;
    }

    // Build filteredCalls by combining previously accumulated selected calls (filteredCalls?.calls)
    // with any matches from the current page so selections from other pages are preserved.
    if (checkedRows?.length > 0) {
      const existingSelected = filteredCalls?.calls ?? [];

      const pageMatches =
        (callData?.data ?? []).filter((call) =>
          call.recordings.some((r) => checkedRows.includes(r.id))
        );

      // Merge and dedupe by call.id (preserve any earlier selections stored in existingSelected)
      const map = new Map<number, typeof pageMatches[0]>();
      existingSelected.forEach((c) => map.set(c.id, c));
      pageMatches.forEach((c) => map.set(c.id, c));
      let matchingCalls = Array.from(map.values());

      // Determine current sort (use server page sort if available, otherwise default to DateTime desc)
      const currentSort = callData?.page?.sort ?? initialPageDetail.sort;
      let sortFieldProp = "callTime";
      switch (currentSort?.sortField) {
        case CallTableSortColumnEnum.DateTime:
          sortFieldProp = "callTime";
          break;
        case CallTableSortColumnEnum.FirstParty:
          sortFieldProp = "firstParty.email";
          break;
        case CallTableSortColumnEnum.SecondParty:
          sortFieldProp = "secondParty.email";
          break;
        case CallTableSortColumnEnum.Duration:
          sortFieldProp = "callDuration";
          break;
        case CallTableSortColumnEnum.Comment:
          sortFieldProp = "recordings[0].totalComments";
          break;
        case CallTableSortColumnEnum.Outcome:
          sortFieldProp = "callResult.name";
          break;
        default:
          sortFieldProp = "callTime";
      }

      const sortDir = (currentSort?.sortDirection ?? "desc") === "asc" ? "asc" : "desc";
      // Apply sorting to the merged selected calls
      matchingCalls = orderBy(matchingCalls, [{ field: sortFieldProp, dir: sortDir }]);

      setFilteredCalls({
        id: "tempIdForShowingCheckedRows",
        page: {
          skip: 0,
          take: callData?.page?.take ?? initialPageDetail.take,
          totalRecords: matchingCalls.length,
          sort:
            callData?.page?.sort ?? {
              sortField: CallTableSortColumnEnum.DateTime,
              sortDirection: "desc",
            },
        },
        calls: matchingCalls,
      });

      setIsShowCheckedRows(true);
    }
  }

  const [isBulkDownloading, setIsBulkDownloading] = useState<boolean>(false);
  const [showBulkLimitDialog, setShowBulkLimitDialog] = useState<boolean>(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleExportDropdown = () => {
    setShowExportDropdown((prev) => !prev);
  };

  const handleExcelExport = () => {
    setShowExportDropdown(false);
    setIsExportDialogOpen(true);
  };

  const handleBulkExport = async () => {
    try {
      setIsBulkDownloading(true);
      if (checkedRows?.length <= 0) {
        swal.fire({
          text: trans.fetchLabelKeyTranslation("NoCallSelectedText", "No calls selected to download. Please select one or more calls."),
          confirmButtonText: "Ok",
        })
      } else if (checkedRows?.length > bulkDownloadRowLimit) {
        swal.fire({
          text: `${trans.fetchLabelKeyTranslation("BulkRowCountWarning", "Selected row count cannot be greater than")}: ${bulkDownloadRowLimit}`,
          confirmButtonText: "Ok",
        })
        return;
      }
      else {
        console.log(callData?.data)
        let totalBytes = 0;
        checkedRows.forEach((recordingId) => {
          const matchingCall = callData?.data?.find(call =>
            call.recordings?.[0]?.id === recordingId
          );

          if (matchingCall) {
            totalBytes += matchingCall.recordings[0].recordingSizeInBytes || 0;
          } else {
            // console.error(`Recording ID ${recordingId} not found in calls`);
          }
        });

        const totalMBs = totalBytes / (1024 * 1024);
        if (totalMBs > bulkDownloadSizeLimitMB) {
          setIsBulkDownloading(false);
          setShowBulkLimitDialog(true);
          return;
        }

        const response = await recordingsService.bulkdownloadV2Request(checkedRows);
        if (response) {
          swal.fire({
            title: `${trans.fetchLabelKeyTranslation("TotalCallsSelectedText", "Total Calls Selected")}: ${checkedRows?.length}`,
            html: `
                  <div style="font-size: 14px; margin-top: 5px;">
                    <div><strong>${trans.fetchLabelKeyTranslation("TotalSizeText", "Total Size")}:</strong> ${totalMBs.toFixed(2)} MB </div>
                    <div style="margin-top: 10px; color: #555;">
                     ${trans.fetchLabelKeyTranslation("ReceiveMailText", "Note: You will receive an email with the download link once the bulk download is ready.")} 
                    </div>
                  </div>
                `,
            confirmButtonText: "Ok",
          })
          handleClearAllRows();
        }
      }
    }
    catch (err) {
      if (err instanceof AxiosError) {
        let errorMessage = err?.response?.data?.error;
        let translatedErrorMessage = trans.fetchLabelKeyTranslation("BulkFailedText", "Bulk Download Failed");

        if (errorMessage.includes("Recordings not found")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("RecordingsNotFoundText", "Recordings not found");
        }
        if (errorMessage.includes("User does not have permission")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("NoPermisisionText", "User does not have permission");
        }
        else if (errorMessage.includes("The total download size exceeds")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("BulkSizeExceededText", "The total download size exceeds");
        }
        else if (errorMessage.includes("Unable to download any recording")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("UnableToDownloadText", "Unable to download any recording");
        }

        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: trans.fetchLabelKeyTranslation("SwtAltErrorTitle", "Error"),
          text: translatedErrorMessage,
        };
        swal.fire(swalOptions);
      }
    }
    finally {
      setIsBulkDownloading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportDropdown]);

  const handleClearAllRows = () => {
    setCheckedRows([]);
    setFilteredCalls(undefined);
    setIsShowCheckedRows(false);
  }
  
  const handleBulkLimitDialogClose = () => {
    setShowBulkLimitDialog(false);
   }
   
  return (
    <div className="callList float-left w-100 maxTableCol m-b-20 mt-n2">
      {trans.translationsLoading && (
        <LoadingOverlay
          customStyle={{ position: "fixed", marginTop: "55px" }}
          themeColor={"light"}
          size={"medium"}
          loadingText={trans.fetchLabelKeyTranslation("SwitchLanguageText", "")}
        />
      )}
      <div className="callListInner">
        <div className="callListBox">
          <div className="row">
            <div className="col-md-12">
              <div className="tableTitle d-flex align-items-center justify-content-between">
                <div>
                  <div className="d-flex">
                  <span className="fs-13 font-weight-semi d-flex align-items-center">
                    {trans.fetchLabelKeyTranslation(
                      "SearchResultsText",
                      "Search Results"
                    )}{" "}
                    ({callData?.page?.totalRecords})
                  </span>

                  {checkedRows?.length > 0 && (
                    <div className="d-flex align-items-center">
                      <span className="mx-2 text-muted">|</span>
                      <span className="font-weight-semi">{trans.fetchLabelKeyTranslation("RecordingsSelectedText", "Recordings selected")}:</span>
                      <span className="btn-link text-decoration-none cursor-pointer font-weight-semi mx-1"
                        onClick={toggleCheckedRowsView}
                        title={isShowCheckedRows ? "Show All Calls" : "Show Checked Rows"}
                      >
                        {checkedRows.length}
                      </span>
                      <span className="btn-link text-decoration-none cursor-pointer font-weight-semi text-danger ml-1"
                        onClick={handleClearAllRows}
                        title="Clear checked rows"
                      >
                        <i className="bi bi-x-circle"></i>
                      </span>
                    </div>
                  )}
                  </div>
                  <div>
                    <span
                      style={{ fontWeight: "500" }}
                      className="fs-10 text-black-8"
                    >
                      {callFilter?.periodType === 4
                        ? callFilter.startDate && callFilter.endDate
                          ? formatDateTime(
                            callFilter.startDate,
                            "MMM DD, YYYY"
                          ) +
                          " - " +
                          formatDateTime(callFilter.endDate, "MMM DD, YYYY")
                          : "Custom"
                        : trans.fetchLabelKeyTranslation(
                          PeriodFilterTypes.find(
                            (e) => e.id === callFilter?.periodType
                          )?.filters?.find(
                            (el) => el.id === callFilter?.filterType
                          )?.translationLabelKey ?? "",
                          PeriodFilterTypes.find(
                            (e) => e.id === callFilter?.periodType
                          )?.filters?.find(
                            (el) => el.id === callFilter?.filterType
                          )?.title ?? ""
                        )}
                    </span>
                  </div>
                </div>
                <div className="buttons-container hov-transparent d-flex align-itmes-center">
                  <div className="searchBox searchCol">
                    <CustomSearchFieldTextInput
                      className="input-search"
                      placeholder={trans.fetchLabelKeyTranslation(
                        "SearchPlaceholder",
                        "Search..."
                      )}
                      value={search}
                      updateValue={setSearch}
                      searchTextChangeHandler={handleSearchChange}
                      handleOnKeyDown={handleSearchTextOnKeyDown}
                      onEscapeKeyFunc={true}
                    />
                  </div>
                  {!filterVisible && (
                    <Tooltip
                      anchorElement={"target"}
                      parentTitle={true}
                      position={"top"}
                      style={{ zIndex: 999 }}
                    >
                      <div className="d-flex align-items-center h-100">
                        <Button
                          className="btn btn-link iconBtn p-0 m-l-10 m-r-5"
                          onClick={handleFilterDialog}
                        >
                          <i
                            className="bi bi-sliders fs-20 line-height-1 text-primary"
                            title={trans.fetchLabelKeyTranslation("FilterTxt", "Filter")}
                          ></i>
                        </Button>
                      </div>
                    </Tooltip>
                  )}

                  <div
                    className="position-relative"
                    ref={dropdownRef}
                    style={{ display: "inline-block" }}
                  >
                    <Tooltip
                      anchorElement={"target"}
                      parentTitle={true}
                      position={"left"}
                      style={{ zIndex: 999 }}
                    >
                      <div className="d-flex align-items-center h-100">
                        <Button
                          className="btn bg-primary text-white m-l-8"
                          onClick={toggleExportDropdown}
                          style={{ height: "28px" }}
                        >
                          <i
                            className="bi bi-download line-height-1"
                            title={trans.fetchLabelKeyTranslation("ExportOrDownloadText", "Export/Download")}
                          ></i>
                        </Button>
                      </div>
                    </Tooltip>

                    {showExportDropdown && (
                      <div className="export-dropdown">
                        <div className="dropdown-item d-flex align-items-start p-2 cursor-pointer border-bottom"
                          onClick={handleExcelExport}
                        >
                          <i className="bi bi-filetype-xlsx mr-2 fs-22 text-primary"></i>
                          <div style={{ lineHeight: "1.5", }}>
                            <p className="mb-0">{trans.fetchLabelKeyTranslation("ExportDataText", "Export data")}
                              <small className="text-muted d-block">{trans.fetchLabelKeyTranslation("ExportInFormatText", "Export in .xlsx format")}</small>
                            </p>
                          </div>
                        </div>
                        {auth?.checkUserAccess(AccessPermissionEnum.DownloadCalls) && (
                          <div className="dropdown-item d-flex align-items-start p-2 cursor-pointer"
                            onClick={handleBulkExport}
                          >
                            <i className="bi bi-download mr-2 fs-22 text-primary"></i>
                            <div style={{ lineHeight: "1.5", }}>
                              <p className="mb-0">{trans.fetchLabelKeyTranslation("DownloadRecordingsText", "Download recordings")}
                                <small className="text-muted d-block">
                                  {trans.fetchLabelKeyTranslation("BulkDownloadText", "Bulk download of {0} recordings.").replace("{0}", String(bulkDownloadRowLimit))}
                                </small>
                              </p>
                            </div>
                          </div>)
                        }
                      </div>
                    )}
                  </div>

                  {
                    isExportDialogOpen && (
                      <ExportCallsDialog
                        checkedRows={checkedRows}
                        setCheckedRows={setCheckedRows}
                        onClose={onCloseExportDialog}
                        callData={callData?.data}
                        totalRecords={callData?.page?.totalRecords}
                        filter={callFilter}
                        handleClearAllRows={handleClearAllRows}
                      />
                    )
                  }

                  {showBulkLimitDialog && (
                    <BulkDownloadLimitDialog
                      bulkDownloadSizeInGb={bulkDownloadSizeLimitMB / 1024}
                      onClose={handleBulkLimitDialogClose}
                    />
                  )}

                </div>
              </div>
            </div>
          </div>
          <Tooltip anchorElement={"target"} position={"top"} parentTitle={true}>
            <div className="mb-5">
              <CallListing
                isBasic={filterVisible}
                loading={loading}
                error={error}
                callData={(isShowCheckedRows && filteredCalls?.calls && filteredCalls?.calls?.length > 0) ? filteredCalls?.calls : callData?.data}
                handleCallFlagUpdate={handleCallFlagUpdate}
                // When showing checked rows use client-side pagination (false),
                // otherwise use server-side pagination (true)
                enableServerSidePagination={!isShowCheckedRows}
                searchTerm={""}
                pageDetail={(isShowCheckedRows && filteredCalls?.calls && filteredCalls?.calls?.length > 0 && filteredCalls?.page) ? filteredCalls.page : callData?.page}
                handlePaginationChange={handlePaginationChange}
                handleSortingChange={handleSortChange}
                isCheckboxEnabled={true}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                setFilteredCalls={setFilteredCalls}
                handleClearAllRows={handleClearAllRows}
              />
            </div>
          </Tooltip>
        </div>
        {filterVisible && (
          <div className="callListBoxFilter">
            <div className="listBoxCard">
              <div className="float-left w-100">
                <div
                  className="float-left w-100 d-flex fs-14 align-items-center justify-content-between font-weight-semi p-t-3 p-r-10 p-b-3 p-l-15"
                  style={{ minHeight: "36px" }}
                >
                  <div className="float-left">
                    <span className="p-r-5">
                      {trans.fetchLabelKeyTranslation("DateText", "Date:")}
                    </span>
                    <span className="panelTitleBadge fs-12">
                      {callFilter?.periodType === 4
                        ? callFilter.startDate && callFilter.endDate
                          ? formatDateTime(
                            callFilter.startDate,
                            "MMM DD, YYYY"
                          ) +
                          " - " +
                          formatDateTime(callFilter.endDate, "MMM DD, YYYY")
                          : "Custom"
                        : trans.fetchLabelKeyTranslation(
                          PeriodFilterTypes.find(
                            (e) => e.id === callFilter?.periodType
                          )?.filters?.find(
                            (el) => el.id === callFilter?.filterType
                          )?.translationLabelKey ?? "",
                          PeriodFilterTypes.find(
                            (e) => e.id === callFilter?.periodType
                          )?.filters?.find(
                            (el) => el.id === callFilter?.filterType
                          )?.title ?? ""
                        )}
                    </span>
                  </div>
                  <span className="float-right">
                    <Tooltip
                      anchorElement={"target"}
                      parentTitle={true}
                      position={"top"}
                      style={{ zIndex: 999 }}
                    >
                      <Button
                        className="buttons-container-button radius-50 p-0"
                        style={{ width: "30px", height: "30px" }}
                        title="Close"
                        onClick={handleFilterDialog}
                      >
                        <i className="bi bi-x-lg fs-20 line-height-1 text-primary"></i>
                      </Button>
                    </Tooltip>
                  </span>
                </div>
                <CallFilterDrawer
                  userMasterData={currentUserData}
                  callFilterData={callFilter}
                  handleCallDataFilter={handleCallDataFilter}
                  handleFilterDialog={handleFilterDialog}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallTable;
